/**
 * Phase 15 — Real-time presence indicator.
 *
 * Renders coloured avatar chips for everyone currently editing the
 * Story (excluding the author's own row), polling the API every 10 s.
 * Sends a heartbeat ping every 10 s so the actor itself shows up in
 * the list for any other collaborator looking at the same Story.
 *
 * No WebSocket dependency — polling is good enough for "who's on this
 * Story" in a CMS workflow where actual concurrent writes are rare.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, type JSX } from 'react';
import { makeApi } from '../../lib/api';
import { useAuthStore } from '../auth/authStore';

interface PresenceIndicatorProps {
  storyUuid: string;
}

const POLL_INTERVAL_MS = 10_000;

export function PresenceIndicator({ storyUuid }: PresenceIndicatorProps): JSX.Element | null {
  const token = useAuthStore((s) => s.token);
  const spaceSlug = useAuthStore((s) => s.spaceSlug);

  const enabled = token !== null && spaceSlug !== null;

  // Heartbeat — every POLL_INTERVAL_MS, push our own presence row.
  const ping = useMutation({
    mutationFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const user = useAuthStore.getState().user;
      const label = user?.email ?? null;
      const result = await api.pingPresence(spaceSlug, storyUuid, label ?? undefined);
      if (result.status !== 'ok') {
        // Throwing would surface in console; silently ignore for ping.
        return null;
      }
      return result.data;
    },
  });

  // Kick off the initial ping when the editor mounts.
  useEffect(() => {
    if (!enabled) return;
    ping.mutate();
    const id = window.setInterval(() => ping.mutate(), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storyUuid]);

  const presenceQuery = useQuery({
    enabled,
    queryKey: ['presence', spaceSlug, storyUuid],
    queryFn: async () => {
      if (token === null || spaceSlug === null) {
        throw new Error('no-auth');
      }
      const api = makeApi(token);
      const result = await api.listPresence(spaceSlug, storyUuid);
      if (result.status !== 'ok') {
        throw new Error('failed');
      }
      return result.data;
    },
    refetchInterval: POLL_INTERVAL_MS,
  });

  const entries = useMemo(() => presenceQuery.data?.data ?? [], [presenceQuery.data]);
  if (!enabled || entries.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="presence-indicator"
      className="flex items-center gap-1 text-xs"
      role="status"
      aria-label="Aktuell aktive Editor:innen"
    >
      <span className="mr-1 text-zinc-500">Aktiv:</span>
      {entries.map((e) => {
        // MEDIUM (security review 2026-05-24): the label is
        // caller-supplied free text — a token holder could
        // self-label as another team member to phish co-editors.
        // Always render the trustworthy token-id alongside the
        // user-supplied label in the tooltip, and use the token-id
        // for initials when no label is set.
        const trustworthyId = `T${e.actor_token_id}`;
        const displayName = e.label ?? trustworthyId;
        const initials = trustworthyId.slice(0, 2);
        const tooltip = e.label
          ? `${trustworthyId} (self-labeled as "${e.label}") — last seen ${e.last_seen_at ?? '?'}`
          : `${trustworthyId} — last seen ${e.last_seen_at ?? '?'}`;
        return (
          <span
            key={e.actor_token_id}
            data-testid={`presence-chip-${e.actor_token_id}`}
            title={tooltip}
            aria-label={`Editor-Sitzung: ${displayName}`}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold text-white shadow-sm ring-1 ring-white"
            style={{ backgroundColor: e.color ?? '#6b7280' }}
          >
            {initials}
          </span>
        );
      })}
    </div>
  );
}
